import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { usePermissions } from '@/hooks/usePermissions';

// Import interface from Layout
interface NavigationItem {
  name: string;
  href: string;
  icon: React.ElementType;
  color?: string;
  permissionCheck?: () => boolean;
  subItems?: NavigationItem[];
  notificationCount?: number;
}

// Base navigation items with permission checks
const createNavigation = (permissionChecks: {
  canAccessDashboard: () => boolean;
  canViewInvoices: () => boolean;
  canViewCustomers: () => boolean;
  canAccessVirtualTerminal: () => boolean;
  canViewTransactions: () => boolean;
  canViewProducts: () => boolean;
  canAccessSettings: () => boolean;
}, user: any | null): NavigationItem[] => {
  // First create an array of common menu items
  const menuItems: NavigationItem[] = [
    { 
      name: 'Dashboard', 
      href: '/app', 
      icon: () => null,
      color: 'from-[hsl(var(--villavie-green))] to-[hsl(var(--villavie-green))]',
      permissionCheck: () => true
    },
    { 
      name: 'Invoices', 
      href: '/app/invoices', 
      icon: () => null,
      color: 'from-[hsl(var(--villavie-blue))] to-[hsl(var(--villavie-blue))]',
      permissionCheck: () => user?.role === 'admin' || user?.role === 'super_admin' || permissionChecks.canViewInvoices()
    },
    { 
      name: 'Customers', 
      href: '/app/customers', 
      icon: () => null,
      color: 'from-[hsl(var(--villavie-teal))] to-[hsl(var(--villavie-teal))]',
      permissionCheck: () => user?.role === 'admin' || user?.role === 'super_admin' || permissionChecks.canViewCustomers()
    },
    { 
      name: 'Virtual Terminal', 
      href: '/app/virtual-terminal', 
      icon: () => null,
      color: 'from-[hsl(var(--villavie-gold))] to-[hsl(var(--villavie-gold))]',
      permissionCheck: () => permissionChecks.canAccessVirtualTerminal()
    },
    { 
      name: 'Transactions', 
      href: '/app/transactions', 
      icon: () => null,
      color: 'from-[hsl(var(--accent))] to-[hsl(var(--accent))]',
      permissionCheck: () => user?.role === 'admin' || user?.role === 'super_admin' || permissionChecks.canViewTransactions()
    },
    { 
      name: 'Products', 
      href: '/app/products', 
      icon: () => null,
      color: 'from-[hsl(var(--primary))] to-[hsl(var(--primary))]',
      permissionCheck: () => user?.role === 'admin' || user?.role === 'super_admin' || permissionChecks.canViewProducts()
    },
    { 
      name: 'Settings', 
      href: '/app/settings', 
      icon: () => null,
      color: 'from-gray-600 to-gray-600',
      permissionCheck: () => user?.role === 'admin' || user?.role === 'super_admin' || permissionChecks.canAccessSettings()
    },
  ];
  
  return menuItems;
};

export function useNavigationItem() {
  const location = useLocation();
  const { user } = useAuth();
  const permissions = usePermissions();
  
  // Create and filter navigation items
  const navigation = useMemo(() => {
    const baseNavigation = createNavigation(permissions, user);
    return baseNavigation.filter(item => !item.permissionCheck || item.permissionCheck());
  }, [permissions, user]);
  
  // Find the active navigation item
  const activeItem = useMemo(() => {
    const currentPath = location.pathname;
    
    // First check for exact matches
    const exactMatch = navigation.find(item => item.href === currentPath);
    if (exactMatch) return exactMatch;
    
    // Then check if current path starts with item path (for nested routes)
    const partialMatch = navigation.find(item => 
      item.href !== '/app' && currentPath.startsWith(item.href)
    );
    if (partialMatch) return partialMatch;
    
    // Default to dashboard if no match found
    return navigation.find(item => item.href === '/app') || navigation[0];
  }, [location.pathname, navigation]);
  
  return { activeItem, navigation };
} 
